import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { IRootState } from "../../../../../../reducer/CombineReducer";
import { CreateScheduleSlice } from "../../../CreateSchedule/redux";
import { EmptyFieldValidation } from "../../ScheduleValidation";
import { IFieldDetails, SchedulingModes } from "../../../CreateSchedule/redux/Types";
import { getDayWeekDefaultValues } from "../../../Helper/Script";
import { DAY_START_LIMIT, DAY_END_LIMIT } from "../../../Helper/Constants";
import DayWeekDetailsView from "./DaysConfig.view";

/**
 * Component will be rendered based on recurrence type. 
 * Responsible for rendering the Day and Week components for monthly, quaterly and yearly
 */
const DaysConfig = (): JSX.Element => {
  const scheduling = useSelector((state: IRootState) => state.scheduling);
  const [day, setDay] = useState<IFieldDetails>({
    "value": 1,
    "valid": false,
    "errorText": ""
  });
  const [dayWeekRadio, setDayWeekRadio] = useState<string>("1");
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const { schedulingDetails } = scheduling;
  const { recurrenceData, recurrenceType } = schedulingDetails;
  const calculation = recurrenceData && recurrenceData.calculation;
  const scheduleMode = scheduling.mode === SchedulingModes.VIEW;
  const disabledDayText = (dayWeekRadio === "2" || scheduleMode);
  const disableTheWeekDrpDwn = (dayWeekRadio === "1" || scheduleMode);

  /**
   * On Radio change will be called
   * Selected Radio option value will be set to react local state
   * Default values for Day text input and The dropdown, week dropdown will be updated in redux
   */
  const onDayRadioChange = (selectedValue: string): void => {
    setDayWeekRadio(selectedValue.toString());
    dispatch(CreateScheduleSlice.actions.setNewScheduleDetails({
      ...scheduling.schedulingDetails,
      recurrenceData: getDayWeekDefaultValues(recurrenceType, recurrenceData, selectedValue)
    }));
    if (day.errorText !== "") {
      setDay({
        "value": 1,
        "valid": false,
        "errorText": ""
      });
    }
  };

  /**
   * On Day text input change will be called
   * Validation - Text input value is validated to be between 1-28
   * Entered text input value will be updated in redux
   */
  const onDayTextInputChange = (e: React.SyntheticEvent<any, Event>): void => {
    const { id, value } = e.currentTarget;
    const { isInvalid, validationText } = EmptyFieldValidation(value, translate, id);
    setDay({
      "value": +value,
      "valid": isInvalid,
      "errorText": validationText
    });
    if (value >= DAY_START_LIMIT && value <= DAY_END_LIMIT) {
      const tempRecurrenceData = {
        ...recurrenceData,
        day: +value
      };
      dispatch(CreateScheduleSlice.actions.setNewScheduleDetails({
        ...scheduling.schedulingDetails,
        recurrenceData: tempRecurrenceData
      }));
    }
  };

  /**
   * On Week dropdown onchange will be called
   * Selected dropdown value will be updated as first item in calculation array in redux
   */
  const onWeekOfTheMonthOptionChange = (selectedValue: string): void => {
    if (calculation) {
      const calculate = calculation.slice();
      if (calculation.length > 0) {
        calculate[0] = +selectedValue;
      } else {
        calculate.push(+selectedValue);
      }
      const tempRecurrenceData = {
        ...recurrenceData,
        calculation: calculate
      };
      dispatch(CreateScheduleSlice.actions.setNewScheduleDetails({
        ...scheduling.schedulingDetails,
        recurrenceData: tempRecurrenceData
      }));
    }
  };

  /**
   * On Week day dropdown onchange will be called
   * Selected dropdown value will be updated as second item in calculation array in redux
   */
  const onWeekDayOfTheMonthOptionChange = (selectedValue: string): void => {
    if (calculation) {
      const calculate = calculation.slice();
      if (calculation.length > 0) {
        calculate[1] = +selectedValue;
      } else {
        calculate.push(+selectedValue);
      }
      const tempRecurrenceData = {
        ...recurrenceData,
        calculation: calculate
      };
      dispatch(CreateScheduleSlice.actions.setNewScheduleDetails({
        ...scheduling.schedulingDetails,
        recurrenceData: tempRecurrenceData
      }));
    }
  };

  return (
    <DayWeekDetailsView onDayRadioChange={onDayRadioChange}
      onDayTextInputChange={onDayTextInputChange}
      onWeekOfTheMonthOptionChange={onWeekOfTheMonthOptionChange}
      onWeekDayOfTheMonthOptionChange={onWeekDayOfTheMonthOptionChange}
      day={day}
      scheduling={scheduling}
      disabledDayText={disabledDayText}
      disableTheWeekDrpDwn={disableTheWeekDrpDwn}
    />
  );
};

export default DaysConfig;
